import { Link } from 'react-router-dom';
import { AlertCircle, BellOff, MailOpen, Send, Star, MessageSquareText } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { MetricCard } from '@/components/reports/MetricCard';
import { Stars } from '@/components/reviews/Stars';
import { useReviewReport, type ReviewSummary } from '@/hooks/useReviewReport';
import { useTimezoneUtils } from '@/hooks/useTimezoneUtils';
import { cn } from '@/lib/utils';

interface ReviewsReportProps {
  startDate: Date;
  endDate: Date;
  timezone: string;
  compareEnabled?: boolean;
}

const pct = (part: number, whole: number) => (whole > 0 ? `${Math.round((part / whole) * 100)}%` : '—');

/** One labelled horizontal bar; width is the share of `max`. */
function BarRow({ label, value, max, hint, barClass = 'bg-primary' }: {
  label: React.ReactNode; value: number; max: number; hint?: string; barClass?: string;
}) {
  const width = max > 0 ? Math.max(value > 0 ? 2 : 0, Math.round((value / max) * 100)) : 0;
  return (
    <div className="grid grid-cols-[7rem_1fr_4.5rem] items-center gap-3 text-sm">
      <div className="truncate text-muted-foreground">{label}</div>
      <div className="h-2.5 rounded-full bg-muted">
        <div className={cn('h-2.5 rounded-full transition-all', barClass)} style={{ width: `${width}%` }} />
      </div>
      <div className="text-right tabular-nums">
        <span className="font-medium">{value}</span>
        {hint && <span className="ml-1 text-xs text-muted-foreground">{hint}</span>}
      </div>
    </div>
  );
}

function Funnel({ s }: { s: ReviewSummary }) {
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">What customers did</CardTitle>
        <CardDescription>
          Of the requests sent in this period. Opens and clicks are tracked for email only; a text shows up once the
          customer taps a star.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <BarRow label="Sent" value={s.sent} max={s.sent} />
        <BarRow label="Emails opened" value={s.opened} max={s.sent} hint={pct(s.opened, s.sentEmail)} barClass="bg-info" />
        <BarRow label="Emails clicked" value={s.clicked} max={s.sent} hint={pct(s.clicked, s.sentEmail)} barClass="bg-info" />
        <BarRow label="Left a rating" value={s.rated} max={s.sent} hint={pct(s.rated, s.sent)} barClass="bg-success" />
      </CardContent>
    </Card>
  );
}

function StarBreakdown({ s }: { s: ReviewSummary }) {
  const max = Math.max(...s.stars, 0);
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Ratings</CardTitle>
        <CardDescription>
          {s.rated > 0
            ? `${s.rated} rating${s.rated === 1 ? '' : 's'} on the star page, averaging ${s.averageRating!.toFixed(1)}.`
            : 'No ratings yet. Ratings appear when the link goes to the 5-star page.'}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {[5, 4, 3, 2, 1].map((n) => (
          <BarRow
            key={n}
            label={<Stars n={n} />}
            value={s.stars[n - 1]}
            max={max}
            hint={pct(s.stars[n - 1], s.rated)}
            barClass={n >= 4 ? 'bg-success' : n === 3 ? 'bg-warning' : 'bg-destructive'}
          />
        ))}
      </CardContent>
    </Card>
  );
}

function Feedback({ s }: { s: ReviewSummary }) {
  const { formatDate } = useTimezoneUtils();
  const rows = s.feedback.slice(0, 10);
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Latest ratings</CardTitle>
        <CardDescription>Newest first. Low ratings are worth a call back.</CardDescription>
      </CardHeader>
      <CardContent>
        {rows.length === 0 ? (
          <div className="flex flex-col items-center gap-2 py-6 text-muted-foreground">
            <MessageSquareText className="h-8 w-8 opacity-30" />
            <p className="text-sm">No ratings in this period.</p>
          </div>
        ) : (
          <ul className="divide-y">
            {rows.map((r) => (
              <li key={r.id} className={cn('flex items-start justify-between gap-4 py-3', (r.rating ?? 5) <= 3 && 'rounded-md bg-destructive/5 px-2')}>
                <div className="min-w-0 space-y-1">
                  <div className="flex items-center gap-2">
                    <Stars n={r.rating ?? 0} />
                    {r.customer_id ? (
                      <Link to={`/customers/${r.customer_id}`} className="truncate text-sm font-medium text-primary hover:underline">
                        {r.customers?.customer_name ?? 'Customer'}
                      </Link>
                    ) : (
                      <span className="truncate text-sm font-medium">{r.customers?.customer_name ?? 'Customer'}</span>
                    )}
                  </div>
                  {r.rating_comment && <p className="text-sm text-muted-foreground">“{r.rating_comment}”</p>}
                </div>
                <div className="shrink-0 text-right text-xs text-muted-foreground">
                  {r.rated_at ? formatDate(r.rated_at, 'd MMM') : ''}
                  {r.job_id && (
                    <div>
                      <Link to={`/jobs/${r.job_id}`} className="hover:underline">View job</Link>
                    </div>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

function NotSent({ s }: { s: ReviewSummary }) {
  const max = Math.max(...s.notSent.map((n) => n.count), 0);
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Not sent</CardTitle>
        <CardDescription>
          {s.notSentTotal > 0
            ? `${s.notSentTotal} paid invoice${s.notSentTotal === 1 ? '' : 's'} in this period did not get a request.`
            : 'Every paid invoice in this period got a request.'}
          {s.waiting > 0 && ` ${s.waiting} still waiting to go out.`}
        </CardDescription>
      </CardHeader>
      {s.notSent.length > 0 && (
        <CardContent className="space-y-3">
          {s.notSent.map((n) => (
            <BarRow key={n.reason} label={<span title={n.reason}>{n.reason}</span>} value={n.count} max={max} barClass="bg-muted-foreground/50" />
          ))}
        </CardContent>
      )}
    </Card>
  );
}

/** Reports → Reviews: how the automated review requests are doing. */
export function ReviewsReport({ startDate, endDate, compareEnabled }: ReviewsReportProps) {
  const { data, isLoading, error, refetch } = useReviewReport(startDate, endDate, compareEnabled);

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-4 md:grid-cols-5">
          {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-24 w-full" />)}
        </div>
        <div className="grid gap-4 md:grid-cols-2">
          <Skeleton className="h-56 w-full" />
          <Skeleton className="h-56 w-full" />
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="py-8 text-center">
        <AlertCircle className="mx-auto mb-3 h-8 w-8 text-destructive" />
        <p className="mb-4 text-muted-foreground">{error instanceof Error ? error.message : 'Could not load reviews'}</p>
        <Button variant="outline" onClick={() => refetch()}>Try again</Button>
      </div>
    );
  }

  const s = data.current;
  const c = data.comparisons;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4 md:grid-cols-5">
        <MetricCard
          title="Requests sent"
          value={s.sent}
          subtitle={[s.sentEmail ? `${s.sentEmail} email` : null, s.sentSms ? `${s.sentSms} SMS` : null].filter(Boolean).join(' · ') || 'None in this period'}
          icon={Send}
          tooltip="Review requests that went out in this period."
          comparison={c.sent}
        />
        <MetricCard
          title="Email open rate"
          value={pct(s.opened, s.sentEmail)}
          subtitle={s.sentEmail ? `${s.opened} of ${s.sentEmail} emails` : 'No emails sent'}
          icon={MailOpen}
          iconBgColor="bg-info/10"
          iconColor="text-info"
          tooltip="Share of review emails the customer opened. Some mail apps hide opens, so this undercounts."
        />
        <MetricCard
          title="Ratings"
          value={s.rated}
          subtitle={s.sent ? `${pct(s.rated, s.sent)} of requests sent` : '—'}
          icon={Star}
          iconBgColor="bg-success/10"
          iconColor="text-success"
          tooltip="Customers who tapped a star on the 5-star page. Reviews left directly on Google are not visible here."
          comparison={c.rated}
        />
        <MetricCard
          title="Average rating"
          value={s.averageRating != null ? s.averageRating.toFixed(1) : '—'}
          subtitle={s.rated ? `from ${s.rated} rating${s.rated === 1 ? '' : 's'}` : 'No ratings yet'}
          icon={Star}
          iconBgColor="bg-warning/10"
          iconColor="text-warning"
          comparison={c.averageRating}
        />
        <MetricCard
          title="Opted out"
          value={data.optedOut}
          subtitle="Customers, all time"
          icon={BellOff}
          iconBgColor="bg-muted"
          iconColor="text-muted-foreground"
          tooltip="Customers who unsubscribed or replied STOP, or whom the office switched off. They are never asked again."
        />
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <Funnel s={s} />
        <StarBreakdown s={s} />
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <Feedback s={s} />
        <NotSent s={s} />
      </div>
    </div>
  );
}
